import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "@/lib/router-compat";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      <div className="container mx-auto max-w-4xl px-4 py-8 flex-1">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6 text-foreground hover:text-foreground/80"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Terug
        </Button>

        <div className="bg-card rounded-lg p-6 md:p-8 text-foreground">
          <h1 className="text-3xl font-bold mb-6">Privacybeleid</h1>
          <p className="text-sm text-muted-foreground mb-8">Laatst bijgewerkt: 15 januari 2026</p>

          <div className="space-y-6 prose prose-sm max-w-none dark:prose-invert">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Inleiding</h2>
              <p>
                Ayemate ("wij", "ons", "onze") respecteert uw privacy en is toegewijd aan het 
                beschermen van uw persoonsgegevens. Dit privacybeleid legt uit hoe wij uw 
                gegevens verzamelen, gebruiken en beschermen wanneer u De Hussel App gebruikt.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Welke gegevens verzamelen wij?</h2>
              <h3 className="text-lg font-medium mb-2">Accountgegevens</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Voornaam en achternaam</li>
                <li>E-mailadres</li>
                <li>Geboortedatum</li>
                <li>Geslacht</li>
                <li>Profielfoto</li>
                <li>Speelsterkte (niveau)</li>
                <li>Clublidmaatschap</li>
              </ul>

              <h3 className="text-lg font-medium mb-2 mt-4">Gebruiksgegevens</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Aanmeldingen voor sessies</li>
                <li>Speelgeschiedenis en statistieken</li>
                <li>App-interacties en voorkeuren</li>
                <li>Apparaat- en browserinformatie</li>
              </ul>

              <h3 className="text-lg font-medium mb-2 mt-4">Clubgegevens (voor clubbeheerders)</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Clubnaam en adresgegevens</li>
                <li>Contactgegevens beheerder</li>
                <li>KvK-nummer en BTW-nummer</li>
                <li>Factuurgegevens</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Waarom verzamelen wij deze gegevens?</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Accountbeheer:</strong> Om uw account aan te maken en te onderhouden.</li>
                <li><strong>Sessie-indeling:</strong> Om u in te delen met geschikte medespelers op basis van niveau en voorkeuren.</li>
                <li><strong>Communicatie:</strong> Om u te informeren over sessies, wijzigingen en belangrijke updates.</li>
                <li><strong>Push-notificaties:</strong> Om u realtime meldingen te sturen over nieuwe rondes en indelingen.</li>
                <li><strong>Facturatie:</strong> Om clubs te factureren voor het gebruik van de App.</li>
                <li><strong>Verbetering:</strong> Om de App te verbeteren en te optimaliseren.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Rechtsgrond voor verwerking</h2>
              <p>Wij verwerken uw gegevens op basis van:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Toestemming:</strong> U geeft toestemming bij het aanmaken van een account.</li>
                <li><strong>Uitvoering overeenkomst:</strong> De verwerking is noodzakelijk voor het leveren van de dienst.</li>
                <li><strong>Gerechtvaardigd belang:</strong> Voor het verbeteren van onze dienstverlening.</li>
                <li><strong>Wettelijke verplichting:</strong> Voor het voldoen aan fiscale verplichtingen.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Met wie delen wij uw gegevens?</h2>
              <p>Wij delen uw gegevens alleen met:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Uw tennisclub:</strong> Clubbeheerders kunnen gegevens van hun leden inzien voor het organiseren van sessies.</li>
                <li><strong>Medespelers:</strong> Andere deelnemers aan dezelfde sessie kunnen beperkte profielgegevens zien (naam, niveau, foto).</li>
                <li><strong>Verwerkers:</strong> Dienstverleners die ons helpen bij het leveren van de App (hosting, e-mail, betalingen).</li>
              </ul>
              <p className="mt-3">
                Wij verkopen uw gegevens nooit aan derden en delen geen gegevens voor marketingdoeleinden.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Gegevensbeveiliging</h2>
              <p>Wij nemen passende technische en organisatorische maatregelen om uw gegevens te beschermen:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Versleutelde verbindingen (HTTPS/TLS)</li>
                <li>Veilige opslag in beveiligde databases</li>
                <li>Beperkte toegang tot persoonsgegevens</li>
                <li>Tweefactorauthenticatie voor beheerders</li>
                <li>Regelmatige beveiligingsaudits</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Bewaartermijnen</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Accountgegevens:</strong> Worden bewaard zolang uw account actief is, plus 30 dagen na verwijdering.</li>
                <li><strong>Sessiegegevens:</strong> Worden 1 jaar bewaard voor statistieken.</li>
                <li><strong>Factuurgegevens:</strong> Worden 7 jaar bewaard conform fiscale wetgeving.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Uw rechten</h2>
              <p>Onder de AVG heeft u de volgende rechten:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Inzage:</strong> U kunt opvragen welke gegevens wij van u hebben.</li>
                <li><strong>Rectificatie:</strong> U kunt onjuiste gegevens laten corrigeren.</li>
                <li><strong>Verwijdering:</strong> U kunt uw account en alle bijbehorende gegevens laten verwijderen. Dit kunt u zelf doen via de Accountinstellingen in de App. Bij verwijdering worden al uw persoonsgegevens, profielfoto's, sessie-aanmeldingen en speelgeschiedenis permanent en onomkeerbaar verwijderd uit onze systemen.</li>
                <li><strong>Beperking:</strong> U kunt de verwerking van uw gegevens laten beperken.</li>
                <li><strong>Overdraagbaarheid:</strong> U kunt uw gegevens in een gangbaar formaat ontvangen.</li>
                <li><strong>Bezwaar:</strong> U kunt bezwaar maken tegen bepaalde verwerkingen.</li>
              </ul>
              <p className="mt-3">
                Om uw rechten uit te oefenen, kunt u contact met ons opnemen via de contactgegevens onderaan dit beleid, 
                of gebruik de zelfbedieningsfuncties in de App (zoals account verwijderen via Accountinstellingen).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Privacy-instellingen</h2>
              <p>In uw accountinstellingen kunt u:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Uw leeftijd verbergen voor andere spelers</li>
                <li>Push-notificaties in- of uitschakelen</li>
                <li>Uw account en alle gegevens verwijderen</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Cookies en tracking</h2>
              <p>
                De App maakt gebruik van essentiële cookies voor de werking van de app en 
                het onthouden van uw voorkeuren. Wij gebruiken geen tracking-cookies of 
                advertentie-cookies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Wijzigingen in dit beleid</h2>
              <p>
                Wij kunnen dit privacybeleid van tijd tot tijd wijzigen. Belangrijke wijzigingen 
                worden via de App gecommuniceerd. De datum van de laatste wijziging staat bovenaan dit document.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Klachten</h2>
              <p>
                Als u een klacht heeft over de verwerking van uw persoonsgegevens, kunt u 
                contact met ons opnemen. U heeft ook het recht om een klacht in te dienen 
                bij de Autoriteit Persoonsgegevens (<a href="https://www.autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.autoriteitpersoonsgegevens.nl</a>).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">13. Contact</h2>
              <p>
                Voor vragen over dit privacybeleid of uw persoonsgegevens kunt u contact opnemen met:
              </p>
              <p className="mt-2">
                Ayemate<br />
                E-mail: <a href="mailto:privacy@ayemate.nl" className="text-primary hover:underline">privacy@ayemate.nl</a><br />
                Website: <a href="https://ayemate.nl" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ayemate.nl</a>
              </p>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
